import React from 'react'
import * as _ from 'lodash'
import { Row, Col, Input, Select } from 'antd'
import { Formik, Form, Field, FieldProps } from 'formik'
import { useQuery, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import MainTitle from '../MainTitle'
import ComparativeChart from '../ComparativeChart'
import IllustratedTexts from '../IllustratedTexts'
import CustomerReviews from '../CustomerReviews'
import NextStep from '../NextStep'
import { COUNTRY_CONFIG_FRAGMENT } from '../../../../Fragments'
import { FicheComparativeType, CountryConfigType } from '../../../../types'
import BlockElement from '../../../../components/BlockElement'
import CentredLoader from '../../../../components/CentredLoader'

const COUNTRY_CONFIG_LIST = gql`
  query listCountryConfigs {
    countryConfigs {
      ...CountryConfigFragment
    }
  }
  ${COUNTRY_CONFIG_FRAGMENT}
`

interface CountryConfigData {
  countryConfigs: Array<CountryConfigType>
}

interface Props {
  ficheComparative?: FicheComparativeType
  onSubmit: (value: any) => any
}

const FicheForm = (props: Props): JSX.Element => {
  const { t } = useTranslation()
  const { loading, data } = useQuery<CountryConfigData>(COUNTRY_CONFIG_LIST)
  const { Option } = Select

  if (loading) return <CentredLoader />
  return (
    <Formik
      initialValues={{
        metaTitle: props.ficheComparative?.metaTitle,
        metaDescription: props.ficheComparative?.metaDescription,
        comparativeChart: _.omit(props.ficheComparative?.comparativeChart, [
          '__typename',
        ]),
        customerReviews: _.omit(props.ficheComparative?.customerReviews, [
          '__typename',
        ]),
        mainTitle: _.omit(props.ficheComparative?.mainTitle, ['__typename']),
        illustratedTexts: _.omit(props.ficheComparative?.illustratedTexts, [
          '__typename',
        ]),
        nextStep: _.omit(props.ficheComparative?.nextStep, ['__typename']),
        countryConfigId: props.ficheComparative?.countryConfig?.id
          ? parseInt(props.ficheComparative?.countryConfig?.id)
          : undefined,
      }}
      onSubmit={props.onSubmit}
    >
      {({ handleSubmit }) => (
        <Form>
          <h1>{t('ficheComparative.title')}</h1>
          <BlockElement>
            <h2>{t('ficheComparative.informations')}</h2>
            <Row gutter={[0, 12]}>
              <Col span={24}>
                <Row gutter={[0, 2]}>
                  <Col span={24}>
                    <label>{t('country')}</label>
                  </Col>
                  <Col span={24}>
                    <Field name="countryConfigId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          style={{ width: '100%' }}
                          defaultValue={field.value?.toString()}
                          onChange={value =>
                            form.setFieldValue(field.name, parseInt(value))
                          }
                        >
                          {data!.countryConfigs.map(countryConfig => {
                            return (
                              <Option
                                key={`contryConfig.${countryConfig.id}`}
                                disabled={
                                  countryConfig.hasFicheComparative &&
                                  countryConfig.id !=
                                    props.ficheComparative?.countryConfig?.id
                                }
                                value={countryConfig.id}
                              >
                                {countryConfig.countryName}
                              </Option>
                            )
                          })}
                        </Select>
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={[0, 2]}>
                  <Col span={24}>
                    <label>{t('metaTitle')}</label>
                  </Col>
                  <Col span={24}>
                    <Field name="metaTitle">
                      {({ field, form }: FieldProps) => (
                        <Input
                          placeholder="Meta titre"
                          onChange={e =>
                            form.setFieldValue(field.name, e.target.value)
                          }
                          defaultValue={field.value}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={[0, 2]}>
                  <Col span={24}>
                    <label>{t('metaDescription')}</label>
                  </Col>
                  <Col span={24}>
                    <Field name="metaDescription">
                      {({ field, form }: FieldProps) => (
                        <Input
                          placeholder="Meta description"
                          onChange={e =>
                            form.setFieldValue(field.name, e.target.value)
                          }
                          defaultValue={field.value}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Col>
            </Row>
          </BlockElement>
          <BlockElement>
            <Field name="mainTitle">
              {({ field, form }: FieldProps) => (
                <MainTitle field={field} form={form} />
              )}
            </Field>
          </BlockElement>
          <BlockElement>
            <Field name="comparativeChart">
              {({ field, form }: FieldProps) => (
                <ComparativeChart field={field} form={form} />
              )}
            </Field>
          </BlockElement>
          <BlockElement>
            <Field name="illustratedTexts">
              {({ field, form }: FieldProps) => (
                <IllustratedTexts field={field} form={form} />
              )}
            </Field>
          </BlockElement>
          <BlockElement>
            <Field name="customerReviews">
              {({ field, form }: FieldProps) => (
                <CustomerReviews field={field} form={form} />
              )}
            </Field>
          </BlockElement>
          <BlockElement>
            <Field name="nextStep">
              {({ field, form }: FieldProps) => (
                <NextStep field={field} form={form} />
              )}
            </Field>
          </BlockElement>
          <Row justify="end">
            <Col span={2}>
              <button type="button" onClick={() => handleSubmit()}>
                {t('save').toUpperCase()}
              </button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  )
}

export default FicheForm
